// BookConsultForm.js - Fully working version matching your API
import React, { useState } from 'react';
import { bookConsultation, verifyConsultationPayment } from '../api/api';

const BookConsultForm = ({ onBookingSuccess, scrollToSection }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    mainHealthConcern: '',
    preferredMode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Custom Toaster Function
  const showToast = (message, type = 'success') => {
    const existingToast = document.querySelector('.custom-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = `custom-toast toast-${type}`;
    
    const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';
    
    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
          ${icon}
        </div>
        <div style="flex: 1;">${message}</div>
        <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
      </div>
    `;
    
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: ${bgColor};
      color: white;
      padding: 14px 20px;
      border-radius: 12px;
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
      min-width: 300px;
      max-width: 400px;
      cursor: pointer;
      animation: slideInRight 0.3s ease;
      z-index: 10000;
    `;
    
    if (!document.querySelector('#toast-styles')) {
      const style = document.createElement('style');
      style.id = 'toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    }, 3000);
    
    toast.onclick = () => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    };
  };

  // Format phone number to Indian format
  const formatPhoneNumber = (phone) => {
    let cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+${cleaned}`;
    }
    if (cleaned.length === 10) {
      return `+91${cleaned}`;
    }
    if (phone.startsWith('+91') && cleaned.length === 12) {
      return `+${cleaned}`;
    }
    return phone;
  };

  // Validate phone number
  const validatePhoneNumber = (phone) => {
    const formattedPhone = formatPhoneNumber(phone);
    const phoneRegex = /^\+91[0-9]{10}$/;
    return phoneRegex.test(formattedPhone);
  };

  const handleInputChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;
    
    if (name === 'phoneNumber') {
      value = value.replace(/\D/g, '');
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const initializeRazorpayPayment = (razorpayOrderId, razorpayKeyId, userData, amount) => {
    if (window.Razorpay) {
      openRazorpay(razorpayOrderId, razorpayKeyId, userData, amount);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => openRazorpay(razorpayOrderId, razorpayKeyId, userData, amount);
    script.onerror = () => {
      showToast('Failed to load payment gateway. Please refresh the page.', 'error');
      setIsLoading(false);
    };
    document.body.appendChild(script);
  };

  const openRazorpay = (razorpayOrderId, razorpayKeyId, userData, amount) => {
    // Convert amount to paise (Razorpay expects amount in paise)
    const amountInPaise = Math.round(amount * 100);
    
    const options = {
      key: razorpayKeyId, // Use key from API response
      amount: amountInPaise, // Amount in paise
      currency: 'INR',
      name: 'Nutrillion',
      description: `Personalized Consultation Fee (Incl. GST)`,
      order_id: razorpayOrderId, // Use the razorpay_order_id from API
      prefill: {
        name: userData.fullName,
        email: userData.email,
        contact: userData.phoneNumber.replace('+91', '')
      },
      theme: {
        color: '#2d5a3d'
      },
      modal: {
        ondismiss: () => {
          showToast('Payment cancelled. You can complete it later.', 'info');
          setIsLoading(false);
        }
      },
      handler: async (paymentResponse) => {
        await verifyPayment(paymentResponse, razorpayOrderId);
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      
      // Add error event handler
      razorpay.on('payment.failed', (response) => {
        console.error('Payment failed:', response);
        showToast(`Payment failed: ${response.error?.description || 'Please try again'}`, 'error');
        setIsLoading(false);
      });
      
      razorpay.open();
    } catch (error) {
      console.error('Razorpay initialization error:', error);
      showToast('Failed to initialize payment. Please try again.', 'error');
      setIsLoading(false);
    }
  };

  const verifyPayment = async (paymentResponse, razorpayOrderId) => {
    showToast('Verifying payment...', 'info');
    
    try {
      const verificationData = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        order_id: razorpayOrderId,
        booking_id: paymentResponse.razorpay_order_id // Or pass your booking_id
      };
      
      const verificationResult = await verifyConsultationPayment(verificationData);
      
      if (verificationResult?.status === true || verificationResult?.success === true) {
        showToast('Payment verified successfully! Your consultation is confirmed.', 'success');
        resetForm();
        if (onBookingSuccess) onBookingSuccess();
      } else {
        showToast(verificationResult?.message || 'Payment verification failed. Please contact support.', 'error');
      }
      
    } catch (error) {
      console.error('Payment verification error:', error);
      showToast('Payment verification failed. Please contact support.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      phoneNumber: '',
      email: '',
      mainHealthConcern: '',
      preferredMode: ''
    });
    setSubmitMessage('');
  };

  // Format currency for display
  const formatIndianCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation checks
    if (!formData.fullName.trim()) {
      showToast('Please enter your full name', 'error');
      return;
    }

    if (!formData.phoneNumber) {
      showToast('Please enter your phone number', 'error');
      return;
    }

    const isValidPhone = validatePhoneNumber(formData.phoneNumber);
    if (!isValidPhone) {
      showToast('Please enter a valid Indian phone number (10 digits, e.g., 9876543210)', 'error');
      return;
    }

    if (!formData.email) {
      showToast('Please enter your email address', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('Please enter a valid email address', 'error');
      return;
    }

    setIsLoading(true);
    setSubmitMessage('Booking your consultation...');

    try {
      const formattedPhone = formatPhoneNumber(formData.phoneNumber);
      
      const consultationPayload = {
        fullName: formData.fullName.trim(),
        phoneNumber: formattedPhone,
        email: formData.email.trim(),
        mainHealthConcern: formData.mainHealthConcern || "Not specified",
        preferredMode: formData.preferredMode || "Video Call",
        source: "web"
      };

      // Call the API
      const response = await bookConsultation(consultationPayload);
      
      console.log('Booking response:', response); // Debug log
      
      // Parse response based on your API structure
      if (response?.success && response?.data) {
        const apiData = response.data;
        
        // Extract required fields from API response
        const razorpayOrderId = apiData.razorpay_order_id;
        const razorpayKeyId = apiData.razorpay_key_id;
        const amountFromApi = apiData.amount; // This is 1178.82 (including GST)
        const originalAmount = apiData.original_amount; // 3000
        const discount = apiData.discount; // 1821.18
        const discountPercentage = apiData.discount_percentage; // 61
        
        console.log('Razorpay Order ID:', razorpayOrderId);
        console.log('Amount to pay:', amountFromApi);
        
        if (razorpayOrderId && razorpayKeyId) {
          showToast('Booking created! Proceeding to payment...', 'success');
          setSubmitMessage('✓ Proceeding to payment...');
          
          // Store discount info in state or context if needed
          localStorage.setItem('consultation_discount', JSON.stringify({
            originalAmount,
            discount,
            discountPercentage,
            finalAmount: amountFromApi
          }));
          
          // Initialize Razorpay with data from API
          initializeRazorpayPayment(
            razorpayOrderId, 
            razorpayKeyId, 
            consultationPayload, 
            amountFromApi
          );
        } else {
          console.error('Missing Razorpay order details:', apiData);
          showToast('Failed to create payment order. Please try again.', 'error');
          setIsLoading(false);
          setSubmitMessage('');
        }
      } else {
        // Handle case where API didn't return success
        console.error('API returned unsuccessful response:', response);
        showToast(response?.message || 'Failed to book consultation. Please try again.', 'error');
        setIsLoading(false);
        setSubmitMessage('');
      }
      
    } catch (error) {
      console.error("Booking error details:", error);
      setSubmitMessage('');
      
      // More detailed error handling
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        if (error.response.data?.errors) {
          const validationError = error.response.data.errors[0];
          showToast(validationError.msg || validationError.message, 'error');
        } else if (error.response.data?.message) {
          showToast(error.response.data.message, 'error');
        } else if (error.response.status === 400) {
          showToast('Invalid request. Please check your details.', 'error');
        } else if (error.response.status === 401) {
          showToast('Payment authentication failed. Please contact support.', 'error');
        } else if (error.response.status === 500) {
          showToast('Server error. Please try again later.', 'error');
        } else {
          showToast('Failed to book consultation. Please try again.', 'error');
        }
      } else if (error.request) {
        showToast('Network error. Please check your connection.', 'error');
      } else {
        showToast(error.message || 'An unexpected error occurred.', 'error');
      }
      
      setIsLoading(false);
    }
  };

  // Calculate display values
  const ORIGINAL_PRICE = 3000;
  const DISCOUNT_AMOUNT = 1821.18;
  const FINAL_PRICE = 1178.82;
  const GST_AMOUNT = 179.82;
  const BASE_PRICE = 999;

  return (
    <div className="booking-card" id="book">
      <div className="booking-card__bar"></div>
      <div className="booking-card__body">
        <div className="booking-card__eyebrow">Limited: 20 Slots This Month</div>
        <h2 className="booking-card__title">Book Your Personalized Consultation</h2>
        <p className="booking-card__sub">Fill in your details — our team will reach out within 2 hours</p>
        
        {submitMessage && (
          <div className="submit-message" style={{
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '8px',
            backgroundColor: submitMessage.includes('✓') ? '#d1fae5' : '#fee2e2',
            color: submitMessage.includes('✓') ? '#065f46' : '#991b1b',
            textAlign: 'center'
          }}>
            {submitMessage}
          </div>
        )}
        
        <form className="form" onSubmit={handleSubmit}>
          <div className="form__group">
            <label className="form__label">Full Name *</label>
            <div className="form__row">
              <iconify-icon icon="lucide:user"></iconify-icon>
              <input 
                type="text" 
                name="fullName"
                placeholder="Enter your full name" 
                value={formData.fullName}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="form__group">
            <label className="form__label">Phone Number *</label>
            <div className="form__row">
              <iconify-icon icon="lucide:phone"></iconify-icon>
              <input 
                type="tel" 
                name="phoneNumber"
                placeholder="9876543210" 
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
            <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
              Enter 10-digit mobile number
            </small>
          </div>
          <div className="form__group">
            <label className="form__label">Email Address *</label>
            <div className="form__row">
              <iconify-icon icon="lucide:mail"></iconify-icon>
              <input 
                type="email" 
                name="email"
                placeholder="you@email.com" 
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="form__group">
            <label className="form__label">Main Health Concern</label>
            <div className="form__row form__row--select">
              <select name="mainHealthConcern" value={formData.mainHealthConcern} onChange={handleInputChange} disabled={isLoading}>
                <option value="">Select your concern</option>
                <option>Obesity / Weight Loss</option>
                <option>Gut / Digestive Issues</option>
                <option>Low Energy / Fatigue</option>
                <option>Hormonal Imbalance / PCOS</option>
                <option>Thyroid</option>
                <option>Pre-Diabetes / Insulin Resistance</option>
                <option>Other</option>
              </select>
              <iconify-icon icon="lucide:chevron-down"></iconify-icon>
            </div>
          </div>
          <div className="form__group">
            <label className="form__label">Preferred Mode</label>
            <div className="form__row form__row--select">
              <select name="preferredMode" value={formData.preferredMode} onChange={handleInputChange} disabled={isLoading}>
                <option value="">Video Call / Phone / In-Person</option>
                <option>Video Call</option>
                <option>Phone</option>
                <option>In-Person</option>
              </select>
              <iconify-icon icon="lucide:chevron-down"></iconify-icon>
            </div>
          </div>
          <button 
            type="submit" 
            className="btn btn--gradient btn--full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span> Processing...
              </>
            ) : (
              <> Book My Consultation <iconify-icon icon="lucide:arrow-right"></iconify-icon></>
            )}
           
          </button>
        </form>
        <div className="booking-card__footer">
              <div>
            <span className="p-strike">₹3,000</span>
            <span className="p-main">₹999</span>
            <span className="p-gst"> + GST</span>
          </div>
          {/* <div className="price-breakdown">
            <div className="price-details">
              <span className="price-label">Original Price:</span>
              <span className="price-value strike">{formatIndianCurrency(ORIGINAL_PRICE)}</span>
            </div>
            <div className="price-details highlight">
              <span className="price-label">Discount (61%):</span>
              <span className="price-value discount">-{formatIndianCurrency(DISCOUNT_AMOUNT)}</span>
            </div>
            <div className="price-details">
              <span className="price-label">Consultation Fee:</span>
              <span className="price-value">{formatIndianCurrency(BASE_PRICE)}</span>
            </div>
            <div className="price-details gst-details">
              <span className="price-label">GST (18%):</span>
              <span className="price-value">{formatIndianCurrency(GST_AMOUNT)}</span>
            </div>
            <div className="price-divider"></div>
            <div className="price-details total-details">
              <span className="price-label total-label">Total Amount:</span>
              <span className="price-value total-value">{formatIndianCurrency(FINAL_PRICE)}</span>
            </div>
          </div> */}
          <div className="secure">
            <iconify-icon icon="lucide:lock"></iconify-icon> 100% Secure  
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookConsultForm;